import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured, checkAuthStatus, clearAuthState } from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  // Using a ref to track if logout is in progress to prevent race conditions
  const logoutInProgress = React.useRef(false);

  // Check for existing session on mount
  useEffect(() => {
    if (isSupabaseConfigured) {
      // Check for Supabase session
      getInitialSession();
      
      // Listen for auth changes
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        async (event, session) => {
          console.log('Auth state changed:', event);
          
          // If logout is in progress, don't process the session
          if (logoutInProgress.current) {
            console.log('Logout in progress, ignoring auth state change');
            return;
          }
          
          if (session?.user) {
            const userData = {
              id: session.user.id,
              email: session.user.email,
              name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0],
              avatar: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(session.user.email?.split('@')[0] || 'User')}&background=3b82f6&color=fff`,
              provider: session.user.app_metadata?.provider,
              createdAt: session.user.created_at
            };
            setUser(userData);
            localStorage.setItem('fahim_user', JSON.stringify(userData));
            
            // Create user database/workspace
            await createUserWorkspace(userData);
          } else {
            setUser(null);
            localStorage.removeItem('fahim_user');
          }
          setLoading(false);
        }
      );

      return () => subscription.unsubscribe();
    } else {
      // Fallback to localStorage for development
      const savedUser = localStorage.getItem('fahim_user');
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (error) {
          console.error('Error parsing saved user:', error);
          localStorage.removeItem('fahim_user');
        }
      }
      setLoading(false);
    }
  }, []);

  const getInitialSession = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const userData = {
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0],
          avatar: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(session.user.email?.split('@')[0] || 'User')}&background=3b82f6&color=fff`,
          provider: session.user.app_metadata?.provider,
          createdAt: session.user.created_at
        };
        setUser(userData);
        localStorage.setItem('fahim_user', JSON.stringify(userData));
        await createUserWorkspace(userData);
      }
    } catch (error) {
      console.error('Error getting session:', error);
    } finally {
      setLoading(false);
    }
  };

  const createUserWorkspace = async (userData) => {
    if (!isSupabaseConfigured) return;
    
    try {
      // Create user profile if it doesn't exist
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userData.id)
        .single();

      if (!existingProfile) {
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: userData.id,
            email: userData.email,
            full_name: userData.name,
            avatar_url: userData.avatar,
            created_at: new Date().toISOString()
          });

        if (profileError) {
          console.error('Error creating profile:', profileError);
        }
      }

      // Create default workspace
      const { data: existingWorkspace } = await supabase
        .from('workspaces')
        .select('*')
        .eq('owner_id', userData.id)
        .single();

      if (!existingWorkspace) {
        const { error: workspaceError } = await supabase
          .from('workspaces')
          .insert({
            name: `${userData.name}'s Workspace`,
            owner_id: userData.id,
            created_at: new Date().toISOString()
          });

        if (workspaceError) {
          console.error('Error creating workspace:', workspaceError);
        }
      }
    } catch (error) {
      console.error('Error setting up user workspace:', error);
    }
  };

  const loginWithGoogle = async () => {
    if (!isSupabaseConfigured) {
      // Fallback for development
      return await mockGoogleLogin();
    }

    setAuthLoading(true);
    setAuthError(null);
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) {
        throw error;
      }

      return { success: true, message: 'Redirecting to Google...' };
    } catch (error) {
      console.error('Google login error:', error);
      setAuthError(error.message);
      return { success: false, message: error.message };
    } finally {
      setAuthLoading(false);
    }
  };

  const mockGoogleLogin = async () => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const mockUser = {
        id: 'google-user-' + Date.now(),
        email: 'user@gmail.com',
        name: 'Google User',
        avatar: 'https://ui-avatars.com/api/?name=Google+User&background=3b82f6&color=fff',
        provider: 'google',
        createdAt: new Date().toISOString()
      };

      setUser(mockUser);
      localStorage.setItem('fahim_user', JSON.stringify(mockUser));
      return { success: true, message: 'Welcome to FAHIM!' };
    } catch (error) {
      setAuthError(error.message);
      return { success: false, message: error.message };
    } finally {
      setAuthLoading(false);
    }
  };

  const login = async (credentials) => {
    // Always use mock login for demo credentials
    if (credentials.email === 'demo@fahim.com' && credentials.password === 'demo123') {
      return await mockLogin(credentials);
    }
    
    if (!isSupabaseConfigured) {
      // Fallback for development
      return await mockLogin(credentials);
    }

    setAuthLoading(true);
    setAuthError(null);
    try {
      const { email, password, name, isSignup } = credentials;

      if (isSignup) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: name,
            }
          }
        });

        if (error) throw error;

        return { success: true, message: 'Account created! Please check your email for verification.' };
      } else {
        // Log more details for debugging
        console.log('Attempting login with:', { email, passwordLength: password?.length });
        
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) {
          console.error('Supabase auth error:', error);
          throw error;
        }

        console.log('Login successful:', data);
        return { success: true, message: 'Welcome back!' };
      }
    } catch (error) {
      console.error('Login error:', error);
      setAuthError(error.message);
      
      // Fallback to mock login for demo user if Supabase login fails
      if (credentials.email === 'demo@fahim.com' && credentials.password === 'demo123') {
        console.log('Falling back to mock login for demo user');
        return await mockLogin(credentials);
      }
      
      return { success: false, message: error.message };
    } finally {
      setAuthLoading(false);
    }
  };

  const mockLogin = async (credentials) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));

      const { email, password, name, isSignup } = credentials;

      if (isSignup) {
        const newUser = {
          id: 'user-' + Date.now().toString(),
          email,
          name,
          createdAt: new Date().toISOString(),
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=3b82f6&color=fff`,
          provider: 'email'
        };
        setUser(newUser);
        localStorage.setItem('fahim_user', JSON.stringify(newUser));
        return { success: true, message: 'Account created successfully!' };
      } else {
        if (email === 'demo@fahim.com' && password === 'demo123') {
          const demoUser = {
            id: 'demo-user',
            email: 'demo@fahim.com',
            name: 'Demo User',
            createdAt: new Date().toISOString(),
            avatar: 'https://ui-avatars.com/api/?name=Demo+User&background=3b82f6&color=fff',
            provider: 'email'
          };
          setUser(demoUser);
          localStorage.setItem('fahim_user', JSON.stringify(demoUser));
          return { success: true, message: 'Welcome to FAHIM!' };
        } else {
          throw new Error('Invalid email or password');
        }
      }
    } catch (error) {
      setAuthError(error.message);
      return { success: false, message: error.message };
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = async () => {
    // Set logout in progress flag to prevent race conditions
    logoutInProgress.current = true;
    
    try {
      console.log('Logging out...');
      
      // First clear user state locally to prevent flickering
      setUser(null);
      
      // Clear all auth data from localStorage
      localStorage.removeItem('fahim_user');
      
      if (isSupabaseConfigured) {
        // Log Supabase signOut attempt
        console.log('Attempting Supabase signOut');
        try {
          const { error } = await supabase.auth.signOut();
          if (error) {
            console.error('Error during Supabase signOut:', error);
          } else {
            console.log('Supabase signOut successful');
          }
        } catch (e) {
          console.error('Exception during Supabase signOut:', e);
        }
      }
      
      // Clear all auth state regardless of Supabase result
      clearAuthState();
      
      console.log('Auth state cleared, redirecting to login page');
      
      // Force redirect to login page
      // This is the most reliable way to ensure complete logout
      window.location.href = '/';
      
    } catch (error) {
      console.error('Logout error:', error);
      
      // Ensure user is logged out even if there's an error
      setUser(null);
      localStorage.removeItem('fahim_user');
      clearAuthState();
      
      // Force redirect to login page
      window.location.href = '/';
    } finally {
      // Reset logout flag (though this won't execute if redirect happens)
      logoutInProgress.current = false;
    }
  };

  const updateProfile = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('fahim_user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    login,
    loginWithGoogle,
    logout,
    updateProfile,
    loading,
    authLoading,
    authError,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};